import { supabase } from '@/integrations/supabase/client';

export interface SampleDataStatus {
  hasSampleData: boolean;
  materialityTopics: number;
  stakeholders: number;
  reports: number;
  settings: boolean;
  insights: number;
  engagements: number;
}

class SampleDataManager {
  private static instance: SampleDataManager;

  public static getInstance(): SampleDataManager {
    if (!SampleDataManager.instance) {
      SampleDataManager.instance = new SampleDataManager();
    }
    return SampleDataManager.instance;
  }

  /**
   * Check if the user has sample data available and their own data status
   */
  async checkSampleDataStatus(): Promise<SampleDataStatus> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          hasSampleData: false,
          materialityTopics: 0,
          stakeholders: 0,
          reports: 0,
          settings: false,
          insights: 0,
          engagements: 0
        };
      }

      // Check for sample data (where organization_id is NULL)
      const [
        sampleTopicsResult,
        sampleStakeholdersResult,
        sampleReportsResult,
        sampleSettingsResult,
        sampleInsightsResult,
        sampleEngagementsResult
      ] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('id', { count: 'exact' })
          .is('organization_id', null),
        supabase
          .from('stakeholders')
          .select('id', { count: 'exact' })
          .is('organization_id', null),
        supabase
          .from('materiality_reports')
          .select('id', { count: 'exact' })
          .is('organization_id', null),
        supabase
          .from('materiality_settings')
          .select('id')
          .is('organization_id', null)
          .single(),
        supabase
          .from('materiality_insights')
          .select('id', { count: 'exact' })
          .is('organization_id', null),
        supabase
          .from('stakeholder_engagements')
          .select('id', { count: 'exact' })
          .is('organization_id', null)
      ]);

      // Check for user's own data (where organization_id is user.id)
      const [
        userTopicsResult,
        userStakeholdersResult,
        userReportsResult,
        userSettingsResult,
        userInsightsResult,
        userEngagementsResult
      ] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('id', { count: 'exact' })
          .eq('organization_id', user.id),
        supabase
          .from('stakeholders')
          .select('id', { count: 'exact' })
          .eq('organization_id', user.id),
        supabase
          .from('materiality_reports')
          .select('id', { count: 'exact' })
          .eq('organization_id', user.id),
        supabase
          .from('materiality_settings')
          .select('id')
          .eq('organization_id', user.id)
          .single(),
        supabase
          .from('materiality_insights')
          .select('id', { count: 'exact' })
          .eq('organization_id', user.id),
        supabase
          .from('stakeholder_engagements')
          .select('id', { count: 'exact' })
          .eq('organization_id', user.id)
      ]);

      const hasSampleData = (
        (sampleTopicsResult.count && sampleTopicsResult.count > 0) ||
        (sampleStakeholdersResult.count && sampleStakeholdersResult.count > 0) ||
        (sampleReportsResult.count && sampleReportsResult.count > 0) ||
        sampleSettingsResult.data ||
        (sampleInsightsResult.count && sampleInsightsResult.count > 0) ||
        (sampleEngagementsResult.count && sampleEngagementsResult.count > 0)
      );

      // Return sample data counts (for display purposes)
      return {
        hasSampleData,
        materialityTopics: sampleTopicsResult.count || 0,
        stakeholders: sampleStakeholdersResult.count || 0,
        reports: sampleReportsResult.count || 0,
        settings: !!sampleSettingsResult.data,
        insights: sampleInsightsResult.count || 0,
        engagements: sampleEngagementsResult.count || 0
      };
    } catch (error) {
      console.error('Error checking sample data status:', error);
      return {
        hasSampleData: false,
        materialityTopics: 0,
        stakeholders: 0,
        reports: 0,
        settings: false,
        insights: 0,
        engagements: 0
      };
    }
  }

  /**
   * Check if the user has their own organization data
   */
  async checkUserDataStatus(): Promise<{
    hasUserData: boolean;
    hasTemporaryData: boolean;
    materialityTopics: number;
    stakeholders: number;
    reports: number;
    settings: boolean;
    insights: number;
    engagements: number;
    temporaryTopics: number;
    temporaryStakeholders: number;
    temporaryReports: number;
    temporarySettings: boolean;
    temporaryInsights: number;
    temporaryEngagements: number;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          hasUserData: false,
          hasTemporaryData: false,
          materialityTopics: 0,
          stakeholders: 0,
          reports: 0,
          settings: false,
          insights: 0,
          engagements: 0,
          temporaryTopics: 0,
          temporaryStakeholders: 0,
          temporaryReports: 0,
          temporarySettings: false,
          temporaryInsights: 0,
          temporaryEngagements: 0
        };
      }

      // Check for real user data (non-temporary)
      const [
        realTopics,
        realStakeholders,
        realReports,
        realSettings,
        realInsights,
        realEngagements
      ] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false),
        supabase
          .from('stakeholders')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false),
        supabase
          .from('materiality_reports')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false),
        supabase
          .from('materiality_settings')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false)
          .single(),
        supabase
          .from('materiality_insights')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false),
        supabase
          .from('stakeholder_engagements')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', false)
      ]);

      // Check for temporary user data
      const [
        tempTopics,
        tempStakeholders,
        tempReports,
        tempSettings,
        tempInsights,
        tempEngagements
      ] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true),
        supabase
          .from('stakeholders')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true),
        supabase
          .from('materiality_reports')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true),
        supabase
          .from('materiality_settings')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true)
          .single(),
        supabase
          .from('materiality_insights')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true),
        supabase
          .from('stakeholder_engagements')
          .select('id')
          .eq('organization_id', user.id)
          .eq('is_temporary', true)
      ]);

      // Handle case where is_temporary column doesn't exist yet
      const hasIsTemporaryColumn = !realTopics.error || realTopics.error.code !== '42703';
      
      let realTopicsCount = 0;
      let realStakeholdersCount = 0;
      let realReportsCount = 0;
      let realSettingsExists = false;
      let realInsightsCount = 0;
      let realEngagementsCount = 0;

      let tempTopicsCount = 0;
      let tempStakeholdersCount = 0;
      let tempReportsCount = 0;
      let tempSettingsExists = false;
      let tempInsightsCount = 0;
      let tempEngagementsCount = 0;

      if (hasIsTemporaryColumn) {
        // Use the is_temporary column
        realTopicsCount = realTopics.data?.length || 0;
        realStakeholdersCount = realStakeholders.data?.length || 0;
        realReportsCount = realReports.data?.length || 0;
        realSettingsExists = !!realSettings.data;
        realInsightsCount = realInsights.data?.length || 0;
        realEngagementsCount = realEngagements.data?.length || 0;

        tempTopicsCount = tempTopics.data?.length || 0;
        tempStakeholdersCount = tempStakeholders.data?.length || 0;
        tempReportsCount = tempReports.data?.length || 0;
        tempSettingsExists = !!tempSettings.data;
        tempInsightsCount = tempInsights.data?.length || 0;
        tempEngagementsCount = tempEngagements.data?.length || 0;
      } else {
        // Fallback: treat all user data as real data (no temporary distinction)
        console.log('is_temporary column not available, treating all user data as real data');
        const allUserData = await Promise.all([
          supabase.from('materiality_topics').select('id').eq('organization_id', user.id),
          supabase.from('stakeholders').select('id').eq('organization_id', user.id),
          supabase.from('materiality_reports').select('id').eq('organization_id', user.id),
          supabase.from('materiality_settings').select('id').eq('organization_id', user.id).single(),
          supabase.from('materiality_insights').select('id').eq('organization_id', user.id),
          supabase.from('stakeholder_engagements').select('id').eq('organization_id', user.id)
        ]);

        realTopicsCount = allUserData[0].data?.length || 0;
        realStakeholdersCount = allUserData[1].data?.length || 0;
        realReportsCount = allUserData[2].data?.length || 0;
        realSettingsExists = !!allUserData[3].data;
        realInsightsCount = allUserData[4].data?.length || 0;
        realEngagementsCount = allUserData[5].data?.length || 0;

        // No temporary data when column doesn't exist
        tempTopicsCount = 0;
        tempStakeholdersCount = 0;
        tempReportsCount = 0;
        tempSettingsExists = false;
        tempInsightsCount = 0;
        tempEngagementsCount = 0;
      }

      const hasUserData = realTopicsCount > 0 || realStakeholdersCount > 0 || realReportsCount > 0 || 
                         realSettingsExists || realInsightsCount > 0 || realEngagementsCount > 0;
      
      const hasTemporaryData = tempTopicsCount > 0 || tempStakeholdersCount > 0 || tempReportsCount > 0 || 
                              tempSettingsExists || tempInsightsCount > 0 || tempEngagementsCount > 0;

      return {
        hasUserData,
        hasTemporaryData,
        materialityTopics: realTopicsCount,
        stakeholders: realStakeholdersCount,
        reports: realReportsCount,
        settings: realSettingsExists,
        insights: realInsightsCount,
        engagements: realEngagementsCount,
        temporaryTopics: tempTopicsCount,
        temporaryStakeholders: tempStakeholdersCount,
        temporaryReports: tempReportsCount,
        temporarySettings: tempSettingsExists,
        temporaryInsights: tempInsightsCount,
        temporaryEngagements: tempEngagementsCount
      };
    } catch (error) {
      console.error('Error checking user data status:', error);
      return {
        hasUserData: false,
        hasTemporaryData: false,
        materialityTopics: 0,
        stakeholders: 0,
        reports: 0,
        settings: false,
        insights: 0,
        engagements: 0,
        temporaryTopics: 0,
        temporaryStakeholders: 0,
        temporaryReports: 0,
        temporarySettings: false,
        temporaryInsights: 0,
        temporaryEngagements: 0
      };
    }
  }

  /**
   * Clear only temporary user data (sample data copies)
   * This preserves any real user data while removing temporary sample copies
   */
  async clearUserData(): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      console.log('Clearing temporary user data (sample copies)...');

      // Clear temporary materiality topics
      const { error: topicsError } = await supabase
        .from('materiality_topics')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (topicsError) throw topicsError;

      // Clear temporary stakeholders
      const { error: stakeholdersError } = await supabase
        .from('stakeholders')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (stakeholdersError) throw stakeholdersError;

      // Clear temporary materiality reports
      const { error: reportsError } = await supabase
        .from('materiality_reports')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (reportsError) throw reportsError;

      // Clear temporary materiality settings
      const { error: settingsError } = await supabase
        .from('materiality_settings')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (settingsError) throw settingsError;

      // Clear temporary materiality insights
      const { error: insightsError } = await supabase
        .from('materiality_insights')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (insightsError) throw insightsError;

      // Clear temporary stakeholder engagements
      const { error: engagementsError } = await supabase
        .from('stakeholder_engagements')
        .delete()
        .eq('organization_id', user.id)
        .eq('is_temporary', true);

      if (engagementsError) throw engagementsError;

      console.log('Temporary user data cleared successfully');

      // Clear MaterialityService cache to ensure fresh data
      const { materialityService } = await import('./materialityService');
      materialityService.clearCache();

      return { success: true };
    } catch (error) {
      console.error('Error clearing temporary user data:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to clear temporary user data' 
      };
    }
  }

  /**
   * Initialize default settings for a new organization
   */
  async initializeDefaultSettings(): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Get user profile to get organization name
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, organization_name')
        .eq('user_id', user.id)
        .single();

      const organizationName = profile?.organization_name || profile?.full_name || 'My Organization';

      // Create default materiality settings
      const defaultSettings = {
        organization_id: user.id,
        organization_name: organizationName,
        industry: 'Technology',
        sector: 'Software & IT Services',
        region: 'Global',
        assessment_frequency: 'annual',
        stakeholder_engagement_method: 'mixed',
        materiality_threshold: 75,
        max_material_topics: 25,
        enable_industry_benchmarking: true,
        enable_stakeholder_mapping: true,
        enable_automated_reporting: true,
        enable_notifications: true,
        default_language: 'en',
        timezone: 'UTC',
        currency: 'USD',
        custom_topics: [],
        excluded_topics: [],
        stakeholder_categories: [
          'Investors',
          'Customers', 
          'Employees',
          'Suppliers',
          'Regulators',
          'Communities',
          'NGOs',
          'Media'
        ],
        reporting_standards: [
          'GRI',
          'SASB',
          'TCFD',
          'CDP'
        ]
      };

      const { error } = await supabase
        .from('materiality_settings')
        .insert([defaultSettings]);

      if (error) throw error;

      // Clear MaterialityService cache to ensure fresh data
      const { materialityService } = await import('./materialityService');
      materialityService.clearCache();

      return { success: true };
    } catch (error) {
      console.error('Error initializing default settings:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to initialize default settings' 
      };
    }
  }

  /**
   * Copy sample data to user's organization as temporary data for exploration
   * This creates temporary copies that can be easily cleared without affecting original sample data
   */
  async copySampleDataToOrganization(force: boolean = false): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user already has any materiality data (only if not forcing)
      if (!force) {
        const { data: existingData, error: existingError } = await supabase
          .from('materiality_topics')
          .select('id')
          .eq('organization_id', user.id)
          .limit(1);

        if (existingError) throw existingError;

        if (existingData && existingData.length > 0) {
          console.log('User already has materiality data, skipping copy');
          return { success: true, error: 'User already has materiality data' };
        }
      }

      console.log('Starting to copy sample data as temporary data for user exploration...');

      // Get sample data (where organization_id is NULL)
      const [
        sampleTopics,
        sampleStakeholders,
        sampleReports,
        sampleSettings,
        sampleInsights,
        sampleEngagements
      ] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('*')
          .is('organization_id', null),
        supabase
          .from('stakeholders')
          .select('*')
          .is('organization_id', null),
        supabase
          .from('materiality_reports')
          .select('*')
          .is('organization_id', null),
        supabase
          .from('materiality_settings')
          .select('*')
          .is('organization_id', null)
          .single(),
        supabase
          .from('materiality_insights')
          .select('*')
          .is('organization_id', null),
        supabase
          .from('stakeholder_engagements')
          .select('*')
          .is('organization_id', null)
      ]);

      console.log('Sample data counts:', {
        topics: sampleTopics.data?.length || 0,
        stakeholders: sampleStakeholders.data?.length || 0,
        reports: sampleReports.data?.length || 0,
        settings: sampleSettings.data ? 1 : 0,
        insights: sampleInsights.data?.length || 0,
        engagements: sampleEngagements.data?.length || 0
      });

      // Copy topics as temporary data
      if (sampleTopics.data && sampleTopics.data.length > 0) {
        const topicsToInsert = sampleTopics.data.map(topic => {
          const { id, ...topicWithoutId } = topic;
          return {
            ...topicWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            created_at: new Date().toISOString()
          };
        });

        const { error: topicsError } = await supabase
          .from('materiality_topics')
          .insert(topicsToInsert);

        if (topicsError) throw topicsError;
        console.log(`Successfully copied ${topicsToInsert.length} materiality topics as temporary data`);
      }

      // Copy stakeholders as temporary data
      if (sampleStakeholders.data && sampleStakeholders.data.length > 0) {
        const stakeholdersToInsert = sampleStakeholders.data.map(stakeholder => {
          const { id, ...stakeholderWithoutId } = stakeholder;
          return {
            ...stakeholderWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            created_at: new Date().toISOString()
          };
        });

        const { error: stakeholdersError } = await supabase
          .from('stakeholders')
          .insert(stakeholdersToInsert);

        if (stakeholdersError) throw stakeholdersError;
        console.log(`Successfully copied ${stakeholdersToInsert.length} stakeholders as temporary data`);
      }

      // Copy reports as temporary data
      if (sampleReports.data && sampleReports.data.length > 0) {
        const reportsToInsert = sampleReports.data.map(report => {
          const { id, ...reportWithoutId } = report;
          return {
            ...reportWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            created_at: new Date().toISOString()
          };
        });

        const { error: reportsError } = await supabase
          .from('materiality_reports')
          .insert(reportsToInsert);

        if (reportsError) throw reportsError;
        console.log(`Successfully copied ${reportsToInsert.length} materiality reports as temporary data`);
      }

      // Copy settings as temporary data
      if (sampleSettings.data) {
        // Check if user already has settings
        const { data: existingSettings, error: checkError } = await supabase
          .from('materiality_settings')
          .select('id')
          .eq('organization_id', user.id)
          .single();

        if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw checkError;
        }

        // Only copy settings if user doesn't already have them
        if (!existingSettings) {
          const { id, ...settingsWithoutId } = sampleSettings.data;
          const settingsToInsert = {
            ...settingsWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            organization_name: 'My Organization (Sample)', // Mark as sample
            created_at: new Date().toISOString()
          };

          const { error: settingsError } = await supabase
            .from('materiality_settings')
            .insert([settingsToInsert]);

          if (settingsError) throw settingsError;
          console.log('Materiality settings copied as temporary data');
        } else {
          console.log('User already has materiality settings, skipping copy');
        }
      }

      // Copy insights as temporary data
      if (sampleInsights.data && sampleInsights.data.length > 0) {
        const insightsToInsert = sampleInsights.data.map(insight => {
          const { id, ...insightWithoutId } = insight;
          return {
            ...insightWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            created_at: new Date().toISOString()
          };
        });

        const { error: insightsError } = await supabase
          .from('materiality_insights')
          .insert(insightsToInsert);

        if (insightsError) throw insightsError;
        console.log(`Successfully copied ${insightsToInsert.length} materiality insights as temporary data`);
      }

      // Copy engagements as temporary data
      if (sampleEngagements.data && sampleEngagements.data.length > 0) {
        const engagementsToInsert = sampleEngagements.data.map(engagement => {
          const { id, ...engagementWithoutId } = engagement;
          return {
            ...engagementWithoutId,
            organization_id: user.id,
            is_temporary: true, // Mark as temporary data
            created_at: new Date().toISOString()
          };
        });

        const { error: engagementsError } = await supabase
          .from('stakeholder_engagements')
          .insert(engagementsToInsert);

        if (engagementsError) throw engagementsError;
        console.log(`Successfully copied ${engagementsToInsert.length} stakeholder engagements as temporary data`);
      }

      console.log('Sample data copied as temporary data for user exploration');

      // Verify that data was actually inserted
      console.log('Verifying data insertion...');
      try {
        const { data: verifyData, error: verifyError } = await supabase
          .from('materiality_topics')
          .select('count')
          .eq('organization_id', user.id)
          .eq('is_temporary', true);

        if (verifyError) {
          console.error('Error verifying data insertion:', verifyError);
          // If is_temporary column doesn't exist, try without it
          if (verifyError.code === '42703') { // Column doesn't exist
            console.log('is_temporary column not found, checking without filter...');
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('materiality_topics')
              .select('count')
              .eq('organization_id', user.id);
            
            if (fallbackError) {
              console.error('Error with fallback query:', fallbackError);
            } else {
              console.log('Verification - User materiality topics count (fallback):', fallbackData?.length || 0);
            }
          }
        } else {
          console.log('Verification - User temporary materiality topics count:', verifyData?.length || 0);
        }
      } catch (error) {
        console.error('Error in verification:', error);
      }

      // Clear MaterialityService cache to ensure fresh data
      console.log('Clearing MaterialityService cache...');
      const { materialityService } = await import('./materialityService');
      materialityService.clearCache();
      console.log('MaterialityService cache cleared successfully');

      return { success: true };
    } catch (error) {
      console.error('Error copying sample data:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to copy sample data' 
      };
    }
  }
}

export const sampleDataManager = SampleDataManager.getInstance(); 